import { formatHourMinuteInZone } from "@/lib/timezone";
import { getSessionDisplayStatus } from "@/lib/session-display-status";

export interface CalendarSession {
  id: string;
  scheduledAt: string;
  durationMinutes: number;
  status: string;
  paymentStatus: string;
  holdExpiresAt?: string | null;
  clientName: string;
  serviceName: string;
  therapistName?: string;
  // "regular" (default) | "emergency" | "group". Emergency and group sessions
  // still occupy the therapist's time but aren't managed like an individual
  // booking (no reschedule/cancel here).
  kind?: "regular" | "emergency" | "group";
  /** Otvoren zahtjev za otkazivanje koji čeka admina; null kad ga nema. */
  cancelRequestId?: string | null;
  cancelRequestedAt?: string | null;
  /** Usluga sa više članova (uključuje klijenta); null = jedna osoba. */
  maxMembers?: number | null;
  /** Broj upisanih ostalih članova seanse. */
  memberCount?: number;
}

export type ViewMode = "week" | "month";

export const STATUS_STYLES: Record<string, { bg: string; border: string; text: string; dot: string }> = {
  scheduled: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800", dot: "bg-blue-500" },
  pending_outcome: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", dot: "bg-amber-500" },
  completed: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", dot: "bg-emerald-500" },
  cancelled: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-400" },
  no_show: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-800", dot: "bg-orange-400" },
  hold: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", dot: "bg-amber-500" },
};

export const STATUS_KEYS: Record<string, string> = {
  scheduled: "calendar.scheduled",
  pending_outcome: "calendar.pendingOutcome",
  completed: "calendar.completed",
  cancelled: "calendar.cancelled",
  no_show: "calendar.noShow",
  hold: "calendar.hold",
};

export function effectiveStatus(s: { status: string; paymentStatus: string; scheduledAt: string }, nowMs = Date.now()): string {
  if (s.status === "scheduled" && s.paymentStatus === "pending_hold") return "hold";
  return getSessionDisplayStatus(s, nowMs);
}

export function startOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

export function fmtDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function parseTime(iso: string): { hour: number; minute: number } {
  // `iso` is a UTC ISO string (with Z suffix) for therapy sessions. Extract the
  // hour/minute as Europe/Sarajevo wall-clock — using the browser's TZ via
  // `getHours()` would render wrong times for any user not in Sarajevo.
  return formatHourMinuteInZone(iso);
}
